import React, { createContext, useContext, useCallback, useMemo } from "react";
import { WebAuth } from "auth0-js";
import * as Sentry from "@sentry/browser";
import { portalAuth } from "./portal_auth_config";
import { useConfig } from "@/core/config";

interface AuthContextV2 {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  logout: () => void;
}

const AuthContextV2 = createContext<AuthContextV2 | null>(null);

interface AuthPortalCustomLoginProviderProps {
  children: React.ReactNode;
  partnerSlug: string;
}

export function useWebAuth(partnerSlug: string) {
  const portalConfig = portalAuth(partnerSlug);
  const config = useConfig();
  const currentPortalAuthConfig = portalConfig[config.environment];
  return useMemo(
    () =>
      new WebAuth({
        clientID: currentPortalAuthConfig.clientId,
        domain: currentPortalAuthConfig.domain,
        audience: currentPortalAuthConfig.audience,
        redirectUri: currentPortalAuthConfig.redirectUri,
        responseType: "token",
        scope: "openid profile email",
      }),
    [
      currentPortalAuthConfig.audience,
      currentPortalAuthConfig.clientId,
      currentPortalAuthConfig.domain,
      currentPortalAuthConfig.redirectUri,
    ]
  );
}

export function AuthPortalCustomLoginProvider({
  children,
  partnerSlug,
}: AuthPortalCustomLoginProviderProps) {
  const webAuth = useWebAuth(partnerSlug);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        return new Promise<void>((resolve, reject) => {
          webAuth.login(
            {
              email,
              password,
              realm: "Username-Password-Authentication",
            },
            (error) => {
              if (error) {
                console.error("Login error:", error);
                Sentry.captureException(error);
                reject(error);
                return;
              }
              resolve();
            }
          );
        });
      } catch (error) {
        console.error("Login error:", error);
        Sentry.captureException(error);
      }
    },
    [webAuth]
  );

  const signup = useCallback(
    async (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        webAuth.signup(
          {
            email,
            password,
            connection: "Username-Password-Authentication",
          },
          (error) => {
            if (error) {
              console.error("Signup error:", error);
              Sentry.captureException(error);
              reject(error);
              return;
            }
            // After successful signup, automatically log the user in
            webAuth.login(
              {
                email,
                password,
                realm: "Username-Password-Authentication",
              },
              (loginError) => {
                if (loginError) {
                  console.error("Auto-login error after signup:", loginError);
                  Sentry.captureException(loginError);
                  reject(loginError);
                  return;
                }
                resolve();
              }
            );
          }
        );
      });
    },
    [webAuth]
  );

  const logout = useCallback(() => {
    webAuth.logout({
      returnTo: `${window.location.origin}/portal/auth/login?partnerSlug=${partnerSlug}`,
    });
  }, [webAuth, partnerSlug]);

  const forgotPassword = useCallback(
    async (email: string) => {
      return new Promise<void>((resolve, reject) => {
        webAuth.changePassword(
          {
            email,
            connection: "Username-Password-Authentication",
          },
          (error) => {
            if (error) {
              console.error("Forgot password error:", error);
              Sentry.captureException(error);
              reject(error);
              return;
            }
            resolve();
          }
        );
      });
    },
    [webAuth]
  );

  const value = useMemo(
    () => ({
      login,
      signup,
      forgotPassword,
      logout,
    }),
    [login, signup, forgotPassword, logout]
  );

  return (
    <AuthContextV2.Provider value={value}>{children}</AuthContextV2.Provider>
  );
}

export function useAuthV2() {
  const context = useContext(AuthContextV2);
  if (!context) {
    throw new Error("useAuthV2 must be used within an AuthPortalProviderV2");
  }
  return context;
}
